export class SequenceBuffer {
  private buffer: Uint16Array;
  private length = 0;

  constructor(capacityStrings = 4096) {
    this.buffer = new Uint16Array(Math.max(2, capacityStrings * 2));
  }

  get count(): number {
    return this.length >>> 1;
  }

  get array(): Uint16Array {
    return this.buffer;
  }

  view(): Uint16Array {
    return this.buffer.subarray(0, this.length);
  }

  toArray(): Uint16Array {
    return this.buffer.slice(0, this.length);
  }

  append(pairs: Uint16Array): void {
    if (pairs.length === 0) return;
    if (pairs.length % 2 !== 0) throw new RangeError('sequence chunks must contain pin pairs');
    this.ensure(this.length + pairs.length);
    this.buffer.set(pairs, this.length);
    this.length += pairs.length;
  }

  replace(sequence: Uint16Array): void {
    if (sequence.length % 2 !== 0) throw new RangeError('sequence must contain pin pairs');
    this.buffer = new Uint16Array(Math.max(2, sequence.length * 2, 8192));
    this.buffer.set(sequence, 0);
    this.length = sequence.length;
  }

  clear(): void {
    this.buffer = new Uint16Array(8192);
    this.length = 0;
  }

  private ensure(needed: number): void {
    if (needed <= this.buffer.length) return;
    let cap = this.buffer.length;
    while (cap < needed) cap *= 2;
    const next = new Uint16Array(cap);
    next.set(this.buffer.subarray(0, this.length), 0);
    this.buffer = next;
  }
}

export class FloatBuffer {
  private buffer: Float32Array;
  private length = 0;

  constructor(capacity = 4096) {
    this.buffer = new Float32Array(Math.max(1, capacity));
  }

  get count(): number {
    return this.length;
  }

  get array(): Float32Array {
    return this.buffer;
  }

  view(): Float32Array {
    return this.buffer.subarray(0, this.length);
  }

  append(values: Float32Array): void {
    if (values.length === 0) return;
    if (this.length + values.length > this.buffer.length) {
      let cap = this.buffer.length;
      while (cap < this.length + values.length) cap *= 2;
      const next = new Float32Array(cap);
      next.set(this.buffer.subarray(0, this.length), 0);
      this.buffer = next;
    }
    this.buffer.set(values, this.length);
    this.length += values.length;
  }

  replace(values: Float32Array): void {
    this.buffer = new Float32Array(Math.max(values.length * 2, 4096));
    this.buffer.set(values, 0);
    this.length = values.length;
  }

  truncate(count: number): void {
    this.length = Math.max(0, Math.min(this.length, count));
  }

  clear(): void {
    this.buffer = new Float32Array(4096);
    this.length = 0;
  }
}
